/**
 * Нейтральное представление сцены между слоями geometry и render (ADR-0006).
 * Не содержит типов three.js — билдеры тестируются без рендера.
 */
export type Vec3 = readonly [number, number, number];

export interface Transform {
  readonly position: Vec3;
  readonly rotation: Vec3;
  readonly scale: Vec3;
}

/**
 * Как подогнать РЕАЛЬНУЮ модель-деталь под размер (детали авторены нормированными
 * по главной оси, сечение — в реальных метрах, см. content-pipeline.md):
 * - `stretch` — тянуть только вдоль `axis` до `length`, сечение натуральное;
 * - `uniform` — равномерно, чтобы наибольший габарит стал `size`.
 * Если модели нет (плейсхолдер) — используется `transform.scale` как габарит бокса.
 */
export type Fit =
  | {
      readonly mode: 'stretch';
      readonly axis: 'x' | 'y' | 'z';
      readonly length: number;
      /** Сечение [Y, Z] в метрах для stretch по X. */
      readonly crossSection?: readonly [number, number];
    }
  | { readonly mode: 'uniform'; readonly size: number };

/**
 * Форма примитива, если для узла нет реальной модели:
 * - box / cylinderX — плейсхолдеры, масштабируются трансформом узла;
 * - beadChain — шариковая цепь вдоль локальной оси Y длиной `transform.scale.y`
 *   (шарики Ø4мм с натуральным размером, не масштабируются).
 * - wallBracket — процедурный L-кронштейн; `scale` = [толщина, высота, вылет] (м).
 */
export type Shape = 'box' | 'cylinderX' | 'beadChain' | 'wallBracket';

export interface SceneNode {
  readonly id: string;
  /** Идентификатор модели-детали (glTF) для инстанцирования, если есть. */
  readonly meshId?: string;
  readonly transform: Transform;
  /** Идентификатор материала из каталога, если есть. */
  readonly materialId?: string;
  /** Подгонка реальной модели под размер (для фурнитуры); плейсхолдер игнорирует. */
  readonly fit?: Fit;
  /** Форма примитива-плейсхолдера (по умолчанию box); для вала — cylinderX. */
  readonly shape?: Shape;
  /** UV по физ. размеру (мозаика фикс. масштаба, без растяжения) — для полотна. */
  readonly uvWorld?: boolean;
  readonly tags?: readonly string[];
  readonly children: readonly SceneNode[];
}

export interface SceneGraph {
  readonly root: SceneNode;
}

export const IDENTITY: Transform = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

/** Трансформ только со смещением (без поворота/масштаба). */
export function translate(position: Vec3): Transform {
  return { position, rotation: [0, 0, 0], scale: [1, 1, 1] };
}

/** Трансформ со смещением и размером (scale = габариты детали в метрах). */
export function boxTransform(position: Vec3, size: Vec3): Transform {
  return { position, rotation: [0, 0, 0], scale: size };
}

export interface NodeSpec {
  id: string;
  meshId?: string | undefined;
  materialId?: string | undefined;
  transform: Transform;
  fit?: Fit | undefined;
  shape?: Shape | undefined;
  uvWorld?: boolean | undefined;
  tags?: readonly string[] | undefined;
  children?: readonly SceneNode[] | undefined;
}

/**
 * Собрать `SceneNode`, опуская необязательные поля со значением `undefined`
 * (совместимо с `exactOptionalPropertyTypes`).
 */
export function makeNode(spec: NodeSpec): SceneNode {
  const node: { -readonly [K in keyof SceneNode]: SceneNode[K] } = {
    id: spec.id,
    transform: spec.transform,
    children: spec.children ?? [],
  };
  if (spec.meshId !== undefined) node.meshId = spec.meshId;
  if (spec.materialId !== undefined) node.materialId = spec.materialId;
  if (spec.fit !== undefined) node.fit = spec.fit;
  if (spec.shape !== undefined) node.shape = spec.shape;
  if (spec.uvWorld !== undefined) node.uvWorld = spec.uvWorld;
  if (spec.tags !== undefined) node.tags = spec.tags;
  return node;
}
